import React from 'react';
import {TextField, Spinner, Icon} from '@shopify/polaris';
import {withI18n, WithI18nProps} from '@shopify/react-i18n';
import debounce from 'lodash/debounce';
import ValidationService from 'services/validation';
import railsData from 'utilities/rails-data';
import i18nArgs from './locales';

const CONFIG = {
  MIN_STORE_NAME_LENGTH: 4,
  VALIDATION_DELAY: 300,
  DOMAIN_REGEX: /\.myshopify\.(com|io)/,
};

const DEFAULT_STATE = {
  loading: false,
  valid: false,
  error: undefined,
  errorMessage: undefined,
};

interface OwnProps {
  autoFocus?: boolean;
  labelHidden?: boolean;
  customErrorMessage?: string;
  onValidateStore(store: string, valid: boolean): void;
}

type CombinedProps = OwnProps & WithI18nProps;

interface State {
  store: string;
  loading: boolean;
  valid: boolean;
  error?: boolean;
  errorMessage?: string;
}

interface ValidationResponse {
  success: boolean;
  message: string | null;
}

class ValidateStoreName extends React.Component<CombinedProps, State> {
  private debouncedValidate: VoidFunction;

  constructor(props: CombinedProps) {
    super(props);

    this.state = {
      ...DEFAULT_STATE,
      store: '',
    };

    this.debouncedValidate = debounce(this.validate, CONFIG.VALIDATION_DELAY);
  }

  onInputChange = (store: string) => {
    this.setState(
      {
        store,
      },
      () => {
        this.props.onValidateStore(store, false);
        this.debouncedValidate();
      }
    );
  };

  validate = () => {
    const {store} = this.state;

    if (store.length >= CONFIG.MIN_STORE_NAME_LENGTH) {
      const formattedStore = store.replace(CONFIG.DOMAIN_REGEX, '');

      this.setState({
        loading: true,
      });

      this.checkAvailability(formattedStore)
        .then(this.processStoreNameResponse)
        .catch(this.onRequestFail);
    } else {
      this.restoreDefaultState();
    }
  };

  checkAvailability = (store: string) => {
    return ValidationService.shopNameAvailability(store, store, 'unavailable');
  };

  processStoreNameResponse = (data: ValidationResponse): void => {
    const {i18n} = this.props;

    let statusValid: boolean;
    let statusErrorMessage: string;

    if (data.success) {
      statusValid = true;
      statusErrorMessage = '';
    } else {
      statusValid = false;
      statusErrorMessage = i18n.translate(
        'ValidateStoreName.storeDoesNotExist'
      );
    }

    this.setState(
      {
        loading: false,
        valid: statusValid,
        error: !statusValid,
        errorMessage: statusErrorMessage,
      },
      () => this.props.onValidateStore(this.state.store, statusValid)
    );
  };

  onRequestFail = () => {
    const {i18n} = this.props;

    this.props.onValidateStore(this.state.store, true);

    this.setState({
      loading: false,
      valid: false,
      error: true,
      errorMessage: i18n.translate('ValidateStoreName.anErrorOccured'),
    });
  };

  restoreDefaultState = () => {
    this.setState(DEFAULT_STATE, () =>
      this.props.onValidateStore(this.state.store, false)
    );
  };

  renderSuffix() {
    const {loading, valid} = this.state;

    if (loading) {
      return <Spinner size="small" />;
    }

    if (valid) {
      return <Icon source="checkmark" color="green" />;
    }

    return null;
  }

  setInitialStoreValue() {
    const lastStoreName = railsData('last_shop_name');

    if (!lastStoreName) return;

    this.setState(
      {
        store: lastStoreName,
      },
      this.debouncedValidate
    );
  }

  componentDidMount() {
    this.setInitialStoreValue();
  }

  render() {
    const {i18n, autoFocus, labelHidden, customErrorMessage} = this.props;
    const {store, error, errorMessage} = this.state;

    return (
      <TextField
        label={i18n.translate('ValidateStoreName.label')}
        labelHidden={labelHidden && labelHidden}
        value={store}
        placeholder={i18n.translate('ValidateStoreName.placeholder')}
        error={customErrorMessage || (error && errorMessage)}
        suffix={this.renderSuffix()}
        onChange={this.onInputChange}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus && autoFocus}
      />
    );
  }
}

export default withI18n(i18nArgs)(ValidateStoreName);
